import { AlertCircle } from "lucide-react";
import { X } from "lucide-react";
import { FileText } from "lucide-react";
import { Award } from "lucide-react";
import { Eye } from "lucide-react";
import { Send } from "lucide-react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import Swal from "sweetalert2";

dayjs.extend(duration);
dayjs.extend(relativeTime);

export function getDurationLabel(startDate, endDate) {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) {
    throw new Error("Invalid date(s)");
  }

  // const diffInYears = Math.round(end.diff(start, "year", true));
  // if (diffInYears >= 1) {
  //   return `${diffInYears} year${diffInYears > 1 ? "s" : ""}`;
  // }

  const diffInMonths = Math.round(end.diff(start, "month", true));
  if (diffInMonths >= 1) {
    return `${diffInMonths} month${diffInMonths > 1 ? "s" : ""}`;
  }

  const diffInWeeks = Math.round(end.diff(start, "week", true));
  if (diffInWeeks >= 1) {
    return `${diffInWeeks} week${diffInWeeks > 1 ? "s" : ""}`;
  }

  const diffInDays = end.diff(start, "day");
  return `${diffInDays} day${diffInDays > 1 ? "s" : ""}`;
}

export const isPathActive = (location, targetPath) => {
  return location.pathname === targetPath || location.pathname.startsWith(`${targetPath}/`);
};

export const formatDate = (dateString) => {
  if (!dateString) return "N/A"
  return new Date(dateString).toLocaleDateString('en-IN')
}
export const getAge = (dateOfBirth) => {
  if (!dateOfBirth) return null
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

export function formatAttendanceDate(date = new Date(), isDateOnly = false) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return isDateOnly ? `${year}-${month}-${day}` : `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export const statusComments = {
  pending: "The application is currently under review. A notification will be provided once a decision is reached.",
  rejected: "The application was not shortlisted for further consideration at this time.",
  accept: "The application has successfully progressed to the next stage.",
  hired: "The candidate has been officially hired for this position.",
  cancelled: "The application process has been cancelled. Please contact support for additional information."
};

// export const isDisabled = (currentStatus, targetStatus) => {
//     if (currentStatus === targetStatus) return true;
//     if (targetStatus === "pending") return currentStatus !== "pending"; 
//     if (targetStatus === "accept") return currentStatus !== "pending"; 
//     // if (targetStatus === "hired") return currentStatus !== "accept"; 
//     // if (targetStatus === "rejected" || targetStatus === "cancelled") return true; 

//     return false;
// };

export const isDisabled = (currentStatus, targetStatus) => {
  if (currentStatus === targetStatus) return true;

  const finalStatuses = ["offer_accept", "offer_declined", 'hired', 'cancelled'];
  if (finalStatuses.includes(currentStatus)) return true;

  if (targetStatus === "pending") return currentStatus !== "pending";

  if (targetStatus === "accept") return currentStatus !== "pending";

  if (targetStatus === "offer_sent") return currentStatus !== "accept";

  if (targetStatus === "hired") return currentStatus !== "accept" && currentStatus !== "offer_accept";

  if (targetStatus === "rejected") return currentStatus === "hired";

  return false;
};

export const excludedStatuses = [
  "hired",
  "accept",
  "cancelled",
  "rejected",
  "offer_accept",
  "offer_declined",
  "offer_sent"
];

export const DisabledMessage = (currentStatus) => {
  if (currentStatus === "offer_accept") return "Student Offer Accepted"
  if (currentStatus === "offer_declined") return "Student Offer Declined"
  if (currentStatus === "cancelled") return "The application process has been cancelled."
  if (currentStatus === "accept") return "Student already shortlisted"
  if (currentStatus === "hired") return "Student already hired";
  if (currentStatus === "offer_sent") return "Offer Already Send."
  if (currentStatus === "rejected") return "Application Rejected."

  return "Application Not Found";
};

export const getStatusBadgeVariant = (status) => {
  switch (status?.toLowerCase()) {
    case "accept":
    case "offer_accept":
      return "success";
    case "rejected":
    case "offer_declined":
      return "danger";
    case "pending":
      return "warning";
    case 'placed':
      return "primary";
    case "hired":
      return "primary";
    case "offer_sent":
      return "custom-offer-sent";
    default:
      return "secondary";
  }
};

export const handleAIJobsTerm = () => {
  Swal.fire({
    title: '<h4 class="mb-3">AI Job – Important Information Before Applying</h4>',
    html: `
      <div class="text-start fw-light" style="font-size: 15px !important; line-height: 20px !important;">
        <p>
          Our <strong>AI Job</strong> feature brings together job listings from multiple trusted platforms,
          allowing you to explore a wide range of opportunities all in one place — no need to visit different websites individually.
        </p>

        <p><strong class="text-dark mb-2">Please note:</strong></p>

        <ul class="list-unstyled">
          <li class="mb-2">• You will be redirected to third-party websites to complete your job application.</li>
          <li class="mb-2">• We are unable to track your application status or updates, as applications are submitted on external platforms.</li>
          <li class="mb-2">• Some job listings may be updated, closed, or removed by the original source at any time without prior notice.</li>
          <li class="mb-2">• This means certain positions you see here might no longer be available when you visit the external website.</li>
          <li class="mb-2">• We recommend checking the posting date and verifying details directly on the source platform before applying.</li>
          <li class="mb-2">• Always review the job details carefully before applying to ensure they match your qualifications and preferences.</li>
          <li>• For your reference, we recommend saving the job link or taking a screenshot after submitting your application.</li>
        </ul>
      </div>
    `,
    icon: "info",
    confirmButtonText: "I Understand",
    confirmButtonColor: "#0d6efd",
    width: 700,
    customClass: {
      popup: "border rounded-4 shadow-lg",
      confirmButton: "btn btn-primary px-4 py-2",
    },
  });
}

export function capitalizeText(input) {
  if (!input) return "";

  return input
    .split(",")
    .map(part => part.trim())
    .filter(Boolean)
    .map(wordGroup =>
      wordGroup
        .split(/\s+/)
        .map(word =>
          word.length > 1
            ? word[0].toUpperCase() + word.slice(1).toLowerCase()
            : word.toUpperCase()
        )
        .join(" ")
    )
    .join(", ");
}



// export const getStatusBadgeVariant = (status) => {
//     switch (status) {
//         case "accept" && "offer_accept":
//             return "success"
//         case "rejected" && "offer_declined":
//             return "danger"
//         case "pending":
//             return "warning"
//         case "hired":
//             return "primary"
//         case "offer_sent":
//             return "primary"
//         default:
//             return "secondary"
//     }
// }

export const statusButtons = [
  { key: "pending", text: "Pending", active: "btn-info", outline: "btn-outline-info", fontSize: "12px", padding: "5px 5px" },
  { key: "accept", text: "Shortlist", active: "btn-success", outline: "btn-outline-success", fontSize: "12px", padding: "5px 5px" },
  { key: "hired", text: "Hired", active: "btn-primary", outline: "btn-outline-primary", fontSize: "12px", padding: "5px 12px" },
  { key: "rejected", text: "Reject", active: "btn-danger", outline: "btn-outline-danger", fontSize: "12px", padding: "5px 5px" },
  { key: "cancelled", text: "Cancel", active: "btn-danger", outline: "btn-outline-danger", fontSize: "12px", padding: "5px 5px" },
];
export const statusOptions = [
  { value: "accept", label: "Shortlisted" },
  { value: "hired", label: "Hired" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
  { value: "offer_accept", label: "Offer Accept" },
  { value: "offer_declined", label: "Offer Declined" },
  { value: "offer_sent", label: "Offer Sent" },
];

export const statusLabels = {
  pending: 'Pending',
  accept: 'Accepted',
  rejected: 'Rejected',
  hired: 'Hired',
  cancelled: 'Cancelled'
};

export const statusColors = {
  pending: 'warning',
  accept: 'success',
  rejected: 'danger',
  hired: 'primary',
  cancelled: 'secondary'
};

export const getStatusIcon = (status) => {
  const icons = {
    pending: <Send size={14} className="text-white" />,
    accept: <Eye size={14} className="text-white" />,
    rejected: <AlertCircle size={14} className="text-white" />,
    hired: <Award size={14} className="text-white" />,
    cancelled: <X size={14} className="text-white" />
  };
  return icons[status] || <FileText size={14} />;
};

export const getStatusBadge = (status) => {
  const color = statusColors[status] || 'secondary';
  const label = statusLabels[status] || status;
  return <span className={`badge bg-${color}`}>{label}</span>;
};

export const formatMinutes = (mins) => {
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${Math.round(mins)}m`;
};
export const formatDateLabel = (dateStr) => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];

  return `${day}-${month}`;
};

// export const getWeekRange = (weeksAgo = 0) => {
//   const today = new Date();
//   const day = today.getDay(); 
//   const diff = (day === 0 ? -6 : 1) - day; 

//   const monday = new Date(today);
//   monday.setDate(today.getDate() + diff - weeksAgo * 7);
//   monday.setHours(0, 0, 0, 0);

//   const sunday = new Date(monday);
//   sunday.setDate(monday.getDate() + 6);
//   sunday.setHours(23, 59, 59, 999);

//   return { start: monday, end: sunday };
// };

export const getWeekRange = (weeksAgo = 0, durationInWeeks = 1) => {
  const today = new Date();
  const day = today.getDay();
  const diff = (day === 0 ? -6 : 1) - day;

  const monday = new Date(today);
  monday.setDate(today.getDate() + diff - weeksAgo * 7);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + (7 * durationInWeeks - 1));
  sunday.setHours(23, 59, 59, 999);

  return { start: monday, end: sunday };
};


export const getMonthRange = () => {
  const today = new Date();

  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  firstDay.setHours(0, 0, 0, 0);

  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  lastDay.setHours(23, 59, 59, 999);

  return { start: firstDay, end: lastDay };
};

export const showStatusMsg = {
  placed: "Pending",
  accept: "Shortlisted",
  offer_sent: "Offer Sent",
  offer_declined: "Offer Declined",
  offer_accept: "Offer Accepted",
}

export const capitalizeName = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";